import { Text } from "rizzui/typography";
import ArrowRight from "../../assets/arrowRight";
import { useForm } from "react-hook-form";
import { Input } from "rizzui/input";
import { ActionIcon } from "rizzui/action-icon";
import { useModal } from "../../hooks/useModal";
import ExtraFields from "./extraFields";
import { useState } from "react";
import BinIcon from "../../assets/bin";
import axios from "axios";
import { baseUrl } from "../account/login";
import { useNotifcication } from "../../hooks/useNotification";
import { Button } from "rizzui/button";

export type type = "HubSpot" | "Google sheet";

interface DataMappingProps {
  type: "HubSpot" | "Google sheet";
  onSubmit?: () => void;
  subText?: string;
  shouldGoBack?: boolean;
  defaultFields: Field[];
  optionalFields?: Field[];
  backFunction?: () => void;
  token: string;
}

export interface Field {
  ringconnect: string;
  [key: string]: string;
}
export interface NotDefinedOptionalField {
  ringconnect: string;
  userInput: string;
  [key: string]: string;
}

export function DataMapping(props: DataMappingProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    unregister,
    setValue,
  } = useForm({});

  const rawMemory = sessionStorage.getItem(
    `Ringconnect_${props.type}_optional_fields`
  );
  let fieldsFromMemory: NotDefinedOptionalField[] = [];

  const { openNotification } = useNotifcication();

  try {
    const parsed = JSON.parse(rawMemory ?? "[]");
    if (Array.isArray(parsed)) {
      fieldsFromMemory = parsed;
    }
  } catch (e) {
    console.warn("Invalid optional fields in sessionStorage");
  }

  const [definedOptionalFields, setDefinedOptionalFields] = useState<Field[]>(
    fieldsFromMemory.filter((f) => !f.userInput)
  );
  const [notDefinedOptionalFields, setNotDefinedOptionalFields] = useState<
    NotDefinedOptionalField[]
  >(fieldsFromMemory.filter((f) => f.userInput));

  const notDefined: type[] = ["Google sheet"];
  const [isLoading, setIsLoading] = useState(false);
  const endpoint = {
    HubSpot: "integrations/hubspot/save_data_mapping?channel=hubspot",
  };

  const handleClick = handleSubmit((data) => {
    setIsLoading(true);
    axios
      .post(
        `${baseUrl}/${endpoint[props.type]}`,
        { data },
        {
          headers: {
            Authorization: `Bearer ${props.token}`,
          },
        }
      )
      .then((response) => {
        if (response?.data?.success) {
          const optionalFieldKeys = [
            ...definedOptionalFields.map((f) => f.ringconnect),
            ...notDefinedOptionalFields.map((f) => f.ringconnect),
          ];

          const optionalData = optionalFieldKeys.map((key) => ({
            ringconnect: key,
            userInput:
              notDefinedOptionalFields.find((f) => f.ringconnect === key)
                ?.userInput || "",
            [props.type]: data[key],
          }));

          sessionStorage.setItem(
            `Ringconnect_${props.type}_optional_fields`,
            JSON.stringify(optionalData)
          );

          props?.onSubmit?.();
        } else {
          openNotification({
            status: "error",
            message: response?.data?.message,
          });
        }
      })
      .catch((e) => {
        openNotification({
          status: "error",
          message: e?.response?.data?.message,
        });
      })
      .finally(() => setIsLoading(false));
  });

  const addMoreField = () => {
    const id = `optional_${notDefinedOptionalFields.length}`;
    const newField: NotDefinedOptionalField = {
      ringconnect: id,
      userInput: "",
      [props.type]: "",
    };
    setNotDefinedOptionalFields((prev) => [...prev, newField]);
  };

  const allFields = [
    ...props.defaultFields.map((f) => f.ringconnect),
    ...notDefinedOptionalFields.map((f) => f.ringconnect),
    ...definedOptionalFields.map((f) => f.ringconnect),
  ];

  const formValues = watch();
  const allFilled = allFields.every(
    (key) => formValues[key]?.trim?.().length > 0
  );
  const { openModal } = useModal();
  return (
    <div className="sm:min-w-[450px] w-fit mx-auto sm:max-w-lg">
      <div className="max-h-[510px] overflow-y-auto customBar w-full border-muted border-[0.5px] rounded-[8px] px-[30px] py-[20px]">
        <div className="flex flex-col gap-[8px]">
          <Text className="font-ibmPlexSerif font-[600] text-[16px] sm:text-[18px] text-primary-dark">
            Data Mapping
          </Text>
          <Text className="font-switzer font-[400] text-[#758599] text-[12px] sm:text-[14px]">
            {props.subText ||
              " Map your CRM fields to RingConnect fields to ensure data syncs correctly."}
          </Text>
        </div>

        <div className="flex flex-col gap-[16px] mt-[24px]">
          <div className="grid grid-cols-6">
            <Text className="font-ibmPlexSerif text-[14px] font-[500] text-primary-dark col-span-3">
              RingConnect
            </Text>

            <Text className="font-ibmPlexSerif text-[14px] font-[500] text-primary-dark col-span-3">
              {props.type}
            </Text>
          </div>
          {props.defaultFields.map((field, index) => (
            <div key={index} className="grid grid-cols-6">
              <div className="col-span-2 bg-[#E4E6E9] dark:bg-muted w-fit px-[8px] py-[4px] max-w-[150px] rounded-[4px] text-[#191919]">
                <p className="font-switzer text-[12px] text-primary-dark font-[400] truncate">
                  {field.ringconnect}
                </p>
              </div>
              <div className="flex justify-center col-span-1">
                <ArrowRight className={"text-[#758599]"} />
              </div>
              <div className="col-span-3">
                <Input
                  type="text"
                  {...register(field.ringconnect, {
                    required: true,
                  })}
                  value={field[props.type]}
                  placeholder={
                    props.type === "Google sheet"
                      ? "Google sheet column name"
                      : ""
                  }
                  disabled={!notDefined.includes(props.type)}
                  error={errors[field.ringconnect] && " "}
                  inputClassName="h-[27px] font-switzer text-[12px] text-primary-dark font-[400] !rounded-[4px] truncate"
                  className="w-full"
                />
              </div>
            </div>
          ))}

          {definedOptionalFields.map((field, index) => (
            <div key={index} className="flex justify-between">
              <div className="w-full grid grid-cols-6">
                <div className="bg-[#E4E6E9] dark:bg-muted w-fit px-[8px] py-[4px] rounded-[4px] text-[#191919] col-span-2">
                  <p className="font-switzer text-[12px] text-primary-dark font-[400]">
                    {field.ringconnect}
                  </p>
                </div>
                <div className="flex justify-center">
                  <ArrowRight className={"text-[#758599]"} />
                </div>
                <div className="col-span-3 relative -mr-[26px]">
                  <div className="flex w-full">
                    <Input
                      type="text"
                      {...register(field.ringconnect, {
                        required: true,
                      })}
                      defaultValue={field[props.type]}
                      disabled={!notDefined.includes(props.type)}
                      error={errors[field.ringconnect] && " "}
                      inputClassName="h-[27px] font-switzer text-[12px] text-primary-dark font-[400] !rounded-[4px] truncate"
                      className="w-full"
                    />

                    <ActionIcon
                      className="!w-fit !h-fit !bg-transparent"
                      onClick={() => {
                        unregister(field.ringconnect);
                        setDefinedOptionalFields((prev) =>
                          prev.filter((f) => f !== field)
                        );
                      }}
                    >
                      <BinIcon className={"text-red-500"} />
                    </ActionIcon>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {notDefinedOptionalFields.map((field, index) => (
            <div key={index} className="flex justify-between">
              <div className="w-full grid grid-cols-6">
                <div className="bg-[#E4E6E9] dark:bg-muted w-fit px-[8px] py-[4px] rounded-[4px] text-[#191919] col-span-2">
                  <p className="font-switzer text-[12px] text-primary-dark font-[400]">
                    {field.userInput?.trim().toLowerCase().replace(/\s+/g, "_")}
                  </p>
                </div>
                <div className="flex justify-center">
                  <ArrowRight className={"text-[#758599]"} />
                </div>
                <div className="col-span-3 relative -mr-[26px]">
                  <div className="flex w-full">
                    <Input
                      type="text"
                      {...register(field.ringconnect, {
                        required: true,
                      })}
                      value={field.userInput}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setNotDefinedOptionalFields((prev) =>
                          prev.map((f) =>
                            f.ringconnect === field.ringconnect
                              ? {
                                  ...f,
                                  userInput: newValue,
                                  [props.type]: newValue,
                                }
                              : f
                          )
                        );
                        setValue(field.ringconnect, newValue);
                      }}
                      placeholder={`${props.type} column name`}
                      error={errors[field.ringconnect] && " "}
                      inputClassName="h-[27px] truncate font-switzer text-[12px] text-primary-dark font-[400] !rounded-[4px]"
                      className="w-full"
                    />

                    <ActionIcon
                      className="!w-fit !h-fit !bg-transparent"
                      onClick={() => {
                        unregister(field.ringconnect);
                        setNotDefinedOptionalFields((prev) =>
                          prev.filter((f) => f !== field)
                        );
                      }}
                    >
                      <BinIcon className={"text-red-500"} />
                    </ActionIcon>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <ActionIcon
          className="!w-fit !h-fit !bg-transparent mt-[12px] -mb-[12px]"
          onClick={() =>
            notDefined.includes(props.type)
              ? addMoreField()
              : openModal({
                  view: (
                    <ExtraFields
                      submitFunction={setDefinedOptionalFields}
                      selectedFields={definedOptionalFields}
                      type={props.type}
                      fields={props.optionalFields}
                    />
                  ),
                })
          }
        >
          <Text className="underline text-primary-dark font-[500] font-switzer text-[12px]">
            + Add more fields
          </Text>
        </ActionIcon>
      </div>
      <div className="w-full flex gap-[26px] justify-between">
        {props.shouldGoBack && (
          <ActionIcon
            className="!w-[200px] !h-[40px] !bg-[#F2F2F2] dark:!bg-muted my-[24px]"
            onClick={props.backFunction}
          >
            <Text className="font-switzer font-[500] text-[12px] text-primary-dark">
              Go back
            </Text>
          </ActionIcon>
        )}

        <Button
          className="!w-full !h-[40px] !bg-btn my-[24px] disabled:!bg-muted"
          onClick={handleClick}
          disabled={notDefined.includes(props.type) && !allFilled}
          isLoading={isLoading}
        >
          <Text className="font-switzer font-[500] text-[12px] text-background">
            Proceed to test connection
          </Text>
        </Button>
      </div>
    </div>
  );
}
